<script>
import HeaderTopbar from 'components/common/HeaderTopbar'
import TextButton from 'components/common/TextButton'
import Message from 'components/common/Message'

import Axios from 'axios'
import { required, minLength, maxLength, email, helpers } from 'vuelidate/lib/validators'

const phonePattern = helpers.regex('phone', /^(\d){11,16}$/)

const LS = window.localStorage
const TIMER_KEY = 'diggMind_trialApply_sms'

export default {
  name: 'GetTrial',
  components: { HeaderTopbar, TextButton, Message },

  data () {
    return {
      info: {
        name: '',
        phone: '',
        code: '',
        email: '',
        corp: '',
        description: ''
      },

      focusing: null,

      timeAwait: -1,
      countdownTimer: null,

      showMessage: false,
      msgText: '',

      submitting: false,
      showConfirmMsg: false
    }
  },

  validations: {
    info: {
      name: {
        required,
        maxLength: maxLength(32)
      },
      phone: {
        required,
        phonePattern,
        minLength: minLength(11),
        maxLength: maxLength(11)
      },
      code: {
        required,
        minLength: minLength(4),
        maxLength: maxLength(8)
      },
      email: {
        required,
        email,
        maxLength: maxLength(256)
      },
      corp: {
        required,
        maxLength: maxLength(128)
      }
    }
  },

  computed: {
    isValid () {
      return !this.$v.info.$invalid
    }
  },

  methods: {
    onFocus (itemName) {
      this.$nextTick(() => {
        this.focusing = itemName
      })
    },

    onBlur () {
      this.focusing = null
    },

    getTimeAwait () {
      const lastTs = +(LS.getItem(TIMER_KEY) || 0)
      const now = Date.now()
      if (lastTs && (lastTs + 600000 > now)) {
        this.timeAwait = Math.ceil((lastTs + 60000 - now) / 1000)
        this.setCountdown()
      } else {
        this.timeAwait = -1
        clearInterval(this.countdownTimer)
      }
    },

    setCountdown () {
      clearInterval(this.countdownTimer)
      this.countdownTimer = setInterval(() => {
        if (this.timeAwait >= 0) {
          this.timeAwait -= 1
        }
        if (this.timeAwait < 0) {
          clearInterval(this.countdownTimer)
        }
      }, 1000)
    },

    getCode () {
      if (this.$v.info.phone.$invalid) { return }
      const now = Date.now()
      LS.setItem(TIMER_KEY, now)
      this.timeAwait = 60
      this.setCountdown()

      Axios.post('https://api.diggmind.com/api/v1/message/sms/send', {
        flag: 'trial.apply',
        mobile: this.info.phone
      }).then(res => {
        if (res && res.data && res.data.http_status === 200) {
          this.msgText = '验证码发送成功，请注意查收。'
          this.showMessage = true
        } else if (res.data.msg) {
          this.msgText = res.data.msg
          this.showMessage = true
        }
      }).catch(err => {
        if (err.response && err.response.data && err.response.data.msg) {
          this.msgText = err.response.data.msg
          this.showMessage = true
        }
      }) 
      
      this.focusCodeInput()
    },

    submit () {
      if (!this.isValid) { return }

      this.submitting = true
      Axios.post('https://api.diggmind.com/api/v2/form/submit', {
        flag: 'trial.apply',
        name: this.info.name,
        phone: this.info.phone,
        valid_code: this.info.code,
        email: this.info.email,
        company: this.info.corp,
        desc: this.info.description
      }).then(() => {
        this.showConfirmMsg = true
        this.clearForm()
        this.submitting = true
      }).catch(err => {
        this.submitting = false
        if (err.response && err.response.data && err.response.data.msg) {
          this.msgText = err.response.data.msg
          this.showMessage = true
        }
        this.clearCodeInput()
      })
    },

    clearCodeInput () {
      this.$set(this.info, 'code', '')
      this.$v.info.code.$reset()
      this.focusCodeInput()
    },

    focusCodeInput () {
      this.$nextTick(() => {
        if (this.$refs && this.$refs.code) {
          this.$refs.code.focus()
        }
      })
    },

    clearForm () {
      this.info = {
        name: '',
        phone: '',
        code: '',
        email: '',
        corp: '',
        description: ''
      }

      this.$v.info.$reset()
    }
  },

  mounted () {
    clearInterval(this.countdownTimer)
    this.getTimeAwait()
  },

  beforeDestroy () {
    clearInterval(this.countdownTimer)
  }
}
</script>

<template lang="pug">
section.get-trial.dm-corp-page
  header-topbar

  section.application-form-container
    .main-container
      form.form-body(onsubmit="return false;" novalidate)
        .form-inner(:class="{'blur': showConfirmMsg}")
          h2 迄今已有超过5,000,000人次使用DiggMind
          h3 马上预约专家互动，了解功能详情、获取试用资格吧！

          fieldset        

            .input-wrapper(:class="{'focusing': focusing === 'name' && info.name.length, 'errored': $v.info.name.$dirty && $v.info.name.$error}"
                          data-label="姓名")
              input(type="text" v-model="info.name"
                    name="name"
                    placeholder="姓名"
                    required
                    maxlength="32"
                    :disabled="submitting"
                    @input="$v.info.name.$touch()"
                    @focus="onFocus('name')"
                    @blur="onBlur")

            .input-wrapper(:class="{'focusing': focusing === 'phone' && info.phone.length, 'errored': $v.info.phone.$dirty && $v.info.phone.$error && info.phone.length >= 11 }"
                          data-label="手机号")
              input(type="tel" v-model="info.phone"
                    name="cellphone"
                    placeholder="手机号"
                    maxlength="11"
                    minlength="11"
                    required
                    :disabled="submitting"
                    @input="$v.info.phone.$touch()"
                    @focus="onFocus('phone')"
                    @blur="onBlur")

            .input-wrapper(:class="{'focusing': focusing === 'code' && info.code.length, 'errored': $v.info.code.$dirty && $v.info.code.$error && info.code.length >= 4}"
                          data-label="手机验证码")
              input(ref="code"
                    type="text" v-model="info.code"
                    placeholder="手机验证码"
                    maxlength="8"
                    minlength="4"
                    required
                    autocomplete="off"
                    :disabled="submitting"
                    @input="$v.info.code.$touch()"
                    @focus="onFocus('code')"
                    @blur="onBlur")
              template(v-if="timeAwait >= 0")
                text-button.get-code(size="small"
                            disabled) 请稍候 ({{ timeAwait }}秒)
              template(v-else)
                text-button.get-code(size="small"
                            :disabled="$v.info.phone.$invalid || submitting"
                            @click="getCode") 获取验证码

            .input-wrapper(:class="{'focusing': focusing === 'email' && info.email.length, 'errored': $v.info.email.$dirty && $v.info.email.$error && info.email.length >= 6}"
                          data-label="邮箱地址")
              input(type="email" v-model="info.email"
                    name="email"
                    placeholder="邮箱地址"
                    maxlength="256"
                    required
                    :disabled="submitting"
                    @input="$v.info.email.$touch()"
                    @focus="onFocus('email')"
                    @blur="onBlur")

            .input-wrapper(:class="{'focusing': focusing === 'corp' && info.corp.length, 'errored': $v.info.corp.$dirty && $v.info.corp.$error}"
                          data-label="单位名称")
              input(type="text" v-model="info.corp"
                    name="corporation"
                    placeholder="所在单位名称"
                    maxlength="128"
                    required
                    :disabled="submitting"
                    @input="$v.info.corp.$touch()"
                    @focus="onFocus('corp')"
                    @blur="onBlur")

            .input-wrapper.textarea(:class="{'focusing': focusing === 'description' && info.description.length}"
                          data-label="需求简述")
              textarea(placeholder="您可以简单描述您的需求吗？"
                      rows="4"
                      maxlength="1024"
                      v-model="info.description"
                      autocomplete="off"
                      :disabled="submitting"
                      @focus="onFocus('description')"
                      @blur="onBlur")

          .button-wrapper
            .hint-text 请完善以上信息并提交，我们的专家会在一个工作日内与您联系。
            text-button(size="large", :disabled="!isValid || submitting" @click="submit") 提交

        transition(name="fade", :duration="500")
          .confirm-message(v-if="showConfirmMsg")
            b 申请信息提交成功
            p 我们的专家会在一个工作日内与您联系

  message(:show.sync="showMessage", :text="msgText")
</template>

<style lang="scss">
@import "../styles/variables";

.get-trial.dm-corp-page {
  background: $bg-light-grayblue;

  .application-form-container {
    padding: 30px 0;

    .form-body {
      width: 90%;
      max-width: 650px;
      margin: 0 auto;
      padding: 40px;
      border-radius: 8px;
      background: #fff;
      box-shadow: 0 3px 10px black(0.05);
      position: relative;
    }

    h2, h3 {
      text-align: center;
    }

    .form-inner {
      transition: filter .3s;
      transition-delay: .2s;
      &.blur {
        filter: blur(8px);
      }
    }
  }

  fieldset {
    border: 0;
    padding: 0;
    margin: 1.5em 0 0 0;
  }

  .input-wrapper {
    margin-top: 1.5em;
    position: relative;

    &:first-of-type {
      margin-top: 0;
    }

    input[type=text],
    input[type=tel],
    input[type=email],
    textarea {
      position: relative;
      z-index: 5;
      width: 100%;
      padding: 0.7em 0.8em;
      border-radius: 6px;
      border: 1px solid $text-light-gray;
      outline: 0;
      background: transparent;

      transition: border-color .3s;
      color: $text-primary;

      &:focus {
        border-color: $primary-color;
      }

      &:disabled,
      &[disabled] {
        background: $bg-light-gray;
        opacity: 0.8;
        pointer-events: none;
      }
    }

    textarea {
      resize: vertical;
    }

    &:before {
      position: absolute;
      left: 0;
      top: 50%;
      z-index: 0;

      content: attr(data-label);
      display: inline-block;
      padding: 0.3em 0.5em;
      background: #000;
      color: #fff;
      border-radius: 3px 0 0 3px;
      opacity: 0;
      transform: translate3d(10%, -50%, 0);

      transition: transform .3s, opacity .3s;
    }

    &.focusing {
      &:before {
        opacity: 1;
        z-index: 0;
        transform: translate3d(-100%, -50%, 0);
      }
    }

    &.textarea {
      &:before {
        top: 0;
        transform: translate3d(10%, 0.4em, 0);
      }

      &.focusing {
        &:before {
          transform: translate3d(-100%, 0.4em, 0);
        }
      }
    }

    &.errored {
      input[type=text],
      input[type=tel],
      input[type=email],
      textarea {
        border-color: $text-hot;
      }
    }
  }

  button.get-code {
    position: absolute;
    right: 0.3em;
    min-width: 8em;
    text-align: center;
    top: 50%;
    z-index: 10;
    transform: translate3d(0, -50%, 0);
  }

  .button-wrapper {
    margin-top: 0.8em;
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    align-items: center;

    .hint-text {
      flex: 1 1 0.00001px;
      margin-right: 1em;
      font-size: 0.85em;
      color: $text-gray;
    }
  }

  .confirm-message {
    @include absFullSpread;
    z-index: 100;
    background: white(0.85);
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 2em;
    line-height: 200%;
    border-radius: 8px;
    overflow: hidden;

    b {
      font-size: 24px;
      padding-bottom: 0.5em;
    }

    p {
      font-size: 18px;
    }
  }
}
</style>